import React, { useState, useEffect, useCallback } from 'react';
import BBALoader from '../../components/BBALoader';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Globe, Star, Share2, Megaphone, Mail, Plus, Trash2 } from 'lucide-react';
import EditButton from '../../components/EditButton';
import PortalBackground from '../../components/PortalBackground';

/**
 * Marketing Access & Logins templates (CRM ▸ Templates ▸ Marketing).
 * Editable getting-started / access instructions per marketing channel.
 * Content is shared team-wide: one Firestore doc per tab in
 * `marketingAccessTemplates/{tabKey}` ({ sections: [{ title, body }] }).
 */

const TABS = [
  { key: 'website', label: 'Website', icon: Globe },
  { key: 'reviews', label: 'Google My Business / Reviews', icon: Star },
  { key: 'social', label: 'Social Media', icon: Share2 },
  { key: 'paid', label: 'Paid Media', icon: Megaphone },
  { key: 'newsletter', label: 'Newsletter', icon: Mail }
];

// Seed content (moved from the portal Website/Reviews Management tabs). Shown
// until a tab is saved for the first time; fully editable after that.
const DEFAULT_SECTIONS = {
  website: [
    {
      title: '1. WordPress Admin Access',
      body: 'If the website is built on WordPress, send admin access to info@reignmediagroup.co with the username reignmediagroup. If the website is not built on WordPress, we will create a WordPress environment for the project.'
    },
    {
      title: '2. Hosting Admin Access',
      body: 'If using GoDaddy, send delegate admin access to parker@reignmediagroup.co. If using another hosting provider, provide hosting admin login credentials. Two-factor authentication should be disabled or a backup login provided.'
    },
    {
      title: '3. Image and Video Assets',
      body: 'Provide project photos, team photos, branding assets, videos, and logos. Access should be shared with parker@reignmediagroup.co and logan@reignmediagroup.co. Google Drive, Dropbox, or similar platforms are preferred.'
    },
    {
      title: '4. Business Contact Information',
      body: 'Provide the business email address and phone number that should appear on the website.'
    },
    {
      title: '5. Business Address',
      body: 'Provide the official business address that should be displayed on the website.'
    },
    {
      title: '6. Brand Fonts and Color Palette',
      body: 'If the business has established branding guidelines, provide preferred fonts, brand colors, logo files (SVG, AI, or EPS if available), and any brand style guide. If not, our team can recommend options.'
    },
    {
      title: '7. List of Services',
      body: 'Provide a complete list of services the company offers, including any new services not currently listed on the website. One service per row.'
    },
    {
      title: '8. Service Areas',
      body: 'Provide a list of the cities the business services. These should be individual city names rather than counties or regions to help with local SEO and location targeting. One city per row.'
    },
    {
      title: '9. Hours of Operation',
      body: 'Provide the official business hours to be displayed on the website.'
    },
    {
      title: '10. Google Business Profile Access',
      body: 'Admin access must be granted to parker@reignmediagroup.co so we can optimize listings, manage reviews, and post updates.'
    },
    {
      title: '11. LinkedIn Login',
      body: 'Provide LinkedIn login credentials. This account will be used exclusively for publishing and distributing blog posts created for the website.'
    }
  ],
  reviews: [
    {
      title: 'Google Business Profile Access',
      body: 'Admin access must be granted to parker@reignmediagroup.co so we can optimize listings, manage reviews, and post updates.'
    }
  ],
  social: [
    {
      title: 'Social Account Access',
      body: 'Provide logins or partner access for each social platform: Facebook Page and Instagram (via Meta Business Suite), LinkedIn, YouTube, and TikTok. Where possible, grant partner/admin access instead of sharing passwords.'
    },
    {
      title: 'Brand Assets',
      body: 'Share logos, brand colors, fonts, and photo/video content for social posts with parker@reignmediagroup.co and logan@reignmediagroup.co. Google Drive, Dropbox, or similar platforms are preferred.'
    }
  ],
  paid: [
    {
      title: 'Google Ads Account Access',
      body: 'Provide the Google Ads customer ID and grant manager (MCC) access, or share admin login credentials.'
    },
    {
      title: 'Meta Ads Account Access',
      body: 'Grant partner access to the Meta Business Manager ad account, or share admin login credentials.'
    },
    {
      title: 'Billing & Budget',
      body: 'Confirm the payment method on each ad account and the approved monthly ad budget before campaigns launch.'
    }
  ],
  newsletter: [
    {
      title: 'Email Platform Access',
      body: 'Provide admin access to the email marketing platform (e.g. Mailchimp, Constant Contact). Invite parker@reignmediagroup.co or share login credentials.'
    },
    {
      title: 'Subscriber List',
      body: 'Export and share the current subscriber/contact list (CSV), or grant access to the CRM where contacts live.'
    },
    {
      title: 'Sending Domain',
      body: 'Provide DNS access or a contact who can add SPF/DKIM records so newsletters send from the company domain.'
    }
  ]
};

const MarketingAccessTemplates = () => {
  const [activeTab, setActiveTab] = useState('website');
  const [content, setContent] = useState({}); // { [tabKey]: sections[] }
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadTab = useCallback(async (tabKey) => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, 'marketingAccessTemplates', tabKey));
      const sections = snap.exists() && Array.isArray(snap.data().sections)
        ? snap.data().sections
        : DEFAULT_SECTIONS[tabKey] || [];
      setContent(prev => ({ ...prev, [tabKey]: sections }));
    } catch (err) {
      console.error('Load marketing access templates error:', err);
      setError('Failed to load content. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (content[activeTab] === undefined) loadTab(activeTab);
  }, [activeTab, content, loadTab]);

  const sections = content[activeTab] || [];

  const startEditing = () => {
    setDraft(sections.map(s => ({ ...s })));
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft([]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const cleaned = draft
        .map(s => ({ title: (s.title || '').trim(), body: (s.body || '').trim() }))
        .filter(s => s.title || s.body);
      await setDoc(doc(db, 'marketingAccessTemplates', activeTab), {
        sections: cleaned,
        updatedAt: new Date().toISOString()
      });
      setContent(prev => ({ ...prev, [activeTab]: cleaned }));
      setEditing(false);
      setDraft([]);
    } catch (err) {
      console.error('Save marketing access templates error:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const switchTab = (key) => {
    if (editing) return; // save or cancel first
    setActiveTab(key);
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2 text-center">
          Marketing Access &amp; Logins
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-6 text-center">
          Getting-started templates: what access, logins, and assets clients provide for each marketing channel
        </p>

        {/* Tab bar */}
        <div className="flex flex-wrap rounded-xl p-1 mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => switchTab(tab.key)}
                disabled={editing && !active}
                className={`flex-1 min-w-[140px] flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? 'bg-white text-gray-900 shadow dark:bg-gray-700 dark:text-white'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                } ${editing && !active ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm text-center">
            {error}
          </div>
        )}

        {loading && sections.length === 0 ? (
          <div className="flex justify-center py-12">
            <BBALoader size={40} />
          </div>
        ) : !editing ? (
          <>
            <div className="flex justify-end mb-4">
              <EditButton label="Edit" onClick={startEditing} />
            </div>
            {sections.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8 text-center">
                <p className="text-gray-500 dark:text-gray-400">
                  No content yet. Click Edit to add sections.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {sections.map((section, i) => (
                  <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
                    {section.title && (
                      <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-1">
                        {section.title}
                      </h3>
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {section.body}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex justify-end gap-2 mb-4">
              <button
                onClick={cancelEditing}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            <div className="space-y-4">
              {draft.map((section, i) => (
                <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => setDraft(prev => prev.map((s, j) => (j === i ? { ...s, title: e.target.value } : s)))}
                      placeholder="Section title"
                      className="flex-1 text-sm font-semibold px-3 py-2 border rounded bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    />
                    <button
                      type="button"
                      onClick={() => setDraft(prev => prev.filter((_, j) => j !== i))}
                      className="p-2 rounded text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                      aria-label="Remove section"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea
                    value={section.body}
                    onChange={(e) => setDraft(prev => prev.map((s, j) => (j === i ? { ...s, body: e.target.value } : s)))}
                    placeholder="Instructions for this step…"
                    rows={4}
                    className="w-full text-sm px-3 py-2 border rounded bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={() => setDraft(prev => [...prev, { title: '', body: '' }])}
                className="w-full flex items-center justify-center gap-1.5 py-3 border border-dashed rounded-lg text-sm border-gray-300 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <Plus className="w-4 h-4" />
                Add section
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketingAccessTemplates;
